import { create } from "zustand";
import { api } from "../utils/api";
import type { Team, TeamMember } from "../types";

interface TeamState {
  teams: Team[];
  activeTeamId: string | null;
  activeTeam: Team | null;
  members: TeamMember[];
  loading: boolean;
  initialized: boolean;

  fetchTeams: () => Promise<void>;
  setActiveTeam: (teamId: string) => void;
  applyTeamUpdate: (team: Team) => void;
  fetchMembers: (teamId: string) => Promise<void>;
  createTeam: (name: string, description?: string) => Promise<Team>;
}

// Tracks the most recent fetchMembers target so a stale in-flight response for a
// previously-selected team can't overwrite the current team's members.
let _lastMembersRequest = "";

export const useTeamStore = create<TeamState>((set, get) => ({
  teams: [],
  activeTeamId: localStorage.getItem("stintsense_active_team"),
  activeTeam: null,
  members: [],
  loading: false,
  initialized: false,

  fetchTeams: async () => {
    set({ loading: true });
    try {
      const teams = await api<Team[]>("/api/teams/me");
      const activeId = get().activeTeamId;
      const active = teams.find((t) => t.id === activeId) || teams[0] || null;
      set({
        teams,
        activeTeam: active,
        activeTeamId: active?.id || null,
        loading: false,
        initialized: true,
        members: active ? get().members : [],
      });
      if (active) {
        localStorage.setItem("stintsense_active_team", active.id);
        get().fetchMembers(active.id);
      } else {
        localStorage.removeItem("stintsense_active_team");
      }
    } catch {
      set({
        loading: false,
        initialized: true,
        teams: [],
        activeTeam: null,
        activeTeamId: null,
        members: [],
      });
    }
  },

  setActiveTeam: (teamId: string) => {
    const prev = get().activeTeamId;
    const team = get().teams.find((t) => t.id === teamId) || null;
    // On an actual team switch, drop the previous team's members immediately so
    // they can't render under the new team while fetchMembers is in flight.
    set({ activeTeamId: teamId, activeTeam: team, ...(teamId !== prev ? { members: [] } : {}) });
    if (teamId) {
      localStorage.setItem("stintsense_active_team", teamId);
    } else {
      localStorage.removeItem("stintsense_active_team");
    }
    if (teamId) get().fetchMembers(teamId);
  },

  applyTeamUpdate: (team: Team) => {
    set((state) => {
      const exists = state.teams.some((t) => t.id === team.id);
      const teams = exists
        ? state.teams.map((t) => (t.id === team.id ? { ...t, ...team } : t))
        : [...state.teams, team];

      const activeTeam =
        state.activeTeamId === team.id || state.activeTeam?.id === team.id
          ? { ...state.activeTeam, ...team }
          : state.activeTeam;

      return { teams, activeTeam };
    });
  },

  fetchMembers: async (teamId: string) => {
    // Last-request-wins: on rapid A->B->A switching, a later-resolving response
    // for a stale team must not overwrite the current one (there is a single
    // `members` slot, so the newest requested team is the correct winner).
    _lastMembersRequest = teamId;
    try {
      const members = await api<TeamMember[]>(`/api/teams/${teamId}/members`);
      if (_lastMembersRequest === teamId) set({ members });
    } catch { /* ignore */ }
  },

  createTeam: async (name: string, description?: string) => {
    const team = await api<Team>("/api/teams", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    });
    await get().fetchTeams();
    get().setActiveTeam(team.id);
    return team;
  },
}));
